import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { DateTime } from "luxon";
import journalApi from "../apis/journalApi";
import registrationApi from "../apis/registrationApi";
import type { RegistrationStep } from "../helpers/registrationStepHelper";
import {
  type AnalysisGroup,
  type IAnalysisGroup,
  invoice,
  selected,
} from "../models/analysisGroup";
import { createAnalysisGroupInstance } from "../models/analysisGroupInstance";
import type { Animal } from "../models/animal";
import {
  createJournal,
  type JournalMeta,
  journalSort,
} from "../models/journal";
import type {
  RegistrationJournalGroup as JournalGroup,
  JournalGroupMeta,
} from "../models/journalGroup";
import type { MailJob } from "../models/mailJob";
import type { LabnumberValidationResult } from "../models/pocos/labnumberValidationResult";
import type { IProduct } from "../models/product";
import {
  type IProductFamily,
  readProductFamilyMeta,
} from "../models/productFamily";
import type { ProductPackage } from "../models/productPackage";
import type { Veterinarian } from "../models/veterinarian";
import { addError, addSuccess } from "./notificationSlice";

export interface RegistrationSliceState {
  selectedProductFamilyId?: number;
  selectedProductId?: number;
  journalGroup: JournalGroup;
  selectedProductAnalyses?: AnalysisGroup[];
  currentStep: RegistrationStep;
  autofills: { [key: string]: string[] };
  registrationId: number | undefined;
  saving: boolean;
  loading: boolean;
  pausedUntil: string | undefined;
}

const initialState: RegistrationSliceState = {
  selectedProductFamilyId: undefined,
  selectedProductId: undefined,
  journalGroup: {},
  selectedProductAnalyses: [],
  currentStep: "analysis",
  autofills: {},
  registrationId: undefined,
  saving: false,
  loading: false,
  pausedUntil: undefined,
};

type ThunkApi = {
  state: { registration: RegistrationSliceState };
};

export const fetchAutofills = createAsyncThunk<void, IProductFamily, ThunkApi>(
  "registration/fetchAutofills",
  async (productFamily: IProductFamily, thunkApi) => {
    const fields = readProductFamilyMeta(productFamily, "fields") ?? {};
    const fetchPromises = [];
    for (const [key, field] of Object.entries(fields)) {
      if (field.type === "text" && field.autocompleteFrom) {
        fetchPromises.push(
          thunkApi.dispatch(
            fetchAutofill({ key, path: field.autocompleteFrom }),
          ),
        );
      }
    }
    await Promise.all(fetchPromises);
  },
);

const fetchAutofill = createAsyncThunk<
  { key: string; content: string[] },
  { key: string; path: string },
  ThunkApi
>(
  "registration/fetchAutofill",
  async (payload: { key: string; path: string }, thunkApi) => {
    const state = thunkApi.getState();
    if (!state.registration.autofills[payload.key]) {
      return {
        key: payload.key,
        content: await registrationApi.fetchAutofill(payload.path),
      };
    } else {
      return {
        key: payload.key,
        content: state.registration.autofills[payload.key],
      };
    }
  },
);

export const clearRegistration = createAsyncThunk<
  {
    product: IProduct | undefined;
    productAnalyses: AnalysisGroup[] | undefined;
    step: RegistrationStep | undefined;
  },
  {
    nextStep: RegistrationStep | undefined;
    product: IProduct | undefined;
    productAnalyses: AnalysisGroup[] | undefined;
  },
  ThunkApi
>(
  "registration/clear",
  async ({ nextStep, product, productAnalyses }, thunkApi) => {
    if (product && productAnalyses) {
      await thunkApi.dispatch(selectProduct({ product, productAnalyses }));
    }

    return { product, productAnalyses, step: nextStep };
  },
);

export const loadJournal = createAsyncThunk<
  JournalGroup,
  {
    journalGroup: JournalGroup | undefined;
  },
  ThunkApi
>("registration/load", async ({ journalGroup }, thunkApi) => {
  if (!journalGroup) {
    return thunkApi.rejectWithValue("");
  }
  return journalGroup;
});

export const doSaveJournal = createAsyncThunk<
  { journalGroup: JournalGroup; clear: boolean },
  {
    journalGroup: JournalGroup;
    clear: boolean;
    nextStep: RegistrationStep;
    product: IProduct | undefined;
    productAnalyses: AnalysisGroup[] | undefined;
  },
  ThunkApi
>(
  "registration/save",
  async (
    { journalGroup, clear, nextStep, product, productAnalyses },
    thunkApi,
  ) => {
    if (!journalGroup) {
      return thunkApi.rejectWithValue("");
    }
    try {
      const result = await registrationApi.update(journalGroup);
      const updatedJournalGroup = result;
      thunkApi.dispatch(
        addSuccess(
          `Journal med journalnummer ${updatedJournalGroup.journalGroupNumber} har sparats`,
        ),
      );

      if (clear) {
        thunkApi.dispatch(
          clearRegistration({ nextStep, product, productAnalyses }),
        );
      } else {
        if (!product) {
          return thunkApi.rejectWithValue("");
        }
        thunkApi.dispatch(selectStep(nextStep));
      }
      return { journalGroup: updatedJournalGroup, clear: clear };
    } catch (_error) {
      thunkApi.dispatch(addError("Journalen kunde inte sparats"));
      return thunkApi.rejectWithValue("");
    }
  },
);

export const pausAnalysis = createAsyncThunk<
  { journalGroup: JournalGroup; pausedUntil: string },
  { journalGroup: JournalGroup },
  ThunkApi
>("registration/pausAnalysis", async ({ journalGroup }, thunkApi) => {
  try {
    const pausedUntilStr = DateTime.now().plus({ day: 1 }).toISODate();
    return { journalGroup, pausedUntil: pausedUntilStr };
  } catch (_error) {
    return thunkApi.rejectWithValue("");
  }
});

export const changeLabnumber = createAsyncThunk<
  { labnumber: number; journalId: number },
  { labnumber: number; journalId: number },
  ThunkApi
>("registration/changeLabnumber", async (arg, thunkApi) => {
  try {
    thunkApi.dispatch(validateLabnumber(arg));
    return arg;
  } catch (_error) {
    return thunkApi.rejectWithValue("");
  }
});

const validateLabnumber = createAsyncThunk<
  { labnumber: number; journalId: number; valid: LabnumberValidationResult },
  { labnumber: number; journalId: number },
  ThunkApi
>("registration/validateLabnumber", async (arg, thunkApi) => {
  try {
    const valid = await journalApi.checkLabnumber(arg.labnumber);
    const state = thunkApi.getState();

    if (
      valid.free &&
      state.registration.journalGroup?.journals?.some(
        (j) => j.id !== arg.journalId && j.labnumber === arg.labnumber,
      )
    ) {
      return { ...arg, valid: { ...valid, free: false } };
    }
    return { ...arg, valid };
  } catch (_error) {
    return thunkApi.rejectWithValue("");
  }
});

type PreregistrationParams = {
  journalGroup: JournalGroup;
  product: IProduct;
  productAnalyses: AnalysisGroup[];
};

const selectInvoiceHelper = (
  state: RegistrationSliceState,
  analysisGroupId: number,
  value: boolean,
) => {
  let invoiceGroups = state.journalGroup.invoiceGroups || [];
  if (value) {
    // checks if there already exists and entry in invoiceGroups with this ID
    const alreadyExists = invoiceGroups.some((ig) => ig.id === analysisGroupId);

    // if it doesn't exist add it
    if (!alreadyExists) {
      invoiceGroups.push({ id: analysisGroupId, code: "" });
    }
  } else {
    invoiceGroups = invoiceGroups.filter((ig) => ig.id !== analysisGroupId);
  }
  return invoiceGroups;
};

export const applyPreregistration = createAsyncThunk<
  JournalGroup,
  PreregistrationParams,
  ThunkApi
>(
  "registration/applyPreregistration",
  async (registration: PreregistrationParams, thunkApi) => {
    await thunkApi.dispatch(
      selectProduct({
        product: registration.product,
        productAnalyses: registration.productAnalyses,
      }),
    );
    return registration.journalGroup;
  },
);

type MetaFieldChange<V, T extends keyof V = keyof V> = {
  fieldName: T;
  value: V[T];
};

let id = -1;
const registrationSlice = createSlice({
  name: "registration",
  initialState,
  reducers: {
    selectProductFamily(state, action: PayloadAction<number>) {
      state.selectedProductFamilyId = action.payload;
      state.journalGroup = {
        productFamilyId: action.payload,
      };
    },
    selectProduct(
      state,
      action: PayloadAction<{
        product: IProduct;
        productAnalyses: AnalysisGroup[];
      }>,
    ) {
      state.journalGroup.productId = action.payload.product.id;
      state.journalGroup.invoiceGroups = createInvoiceGroups(
        action.payload.productAnalyses,
        action.payload.product,
      );
      if ((state.journalGroup.journals ?? []).length === 0) {
        state.journalGroup.journals = [
          createJournal({
            id: id--,
            analysisGroupInstances: action.payload.productAnalyses.map((ag) =>
              createAnalysisGroupInstance({
                analysisGroupId: ag.id,
                selected: selected(ag, action.payload.product),
                invoice: invoice(ag, action.payload.product),
              }),
            ),
          }),
        ];
      } else {
        for (const journal of state.journalGroup.journals ?? []) {
          journal.analysisGroupInstances = action.payload.productAnalyses.map(
            (ag) =>
              createAnalysisGroupInstance({
                analysisGroupId: ag.id,
                selected: selected(ag, action.payload.product),
                invoice: invoice(ag, action.payload.product),
              }),
          );
        }
      }
      state.selectedProductId = action.payload.product.id;
      state.selectedProductAnalyses = action.payload.productAnalyses.map(
        (ag) => ({ ...ag, selected: selected(ag, action.payload.product) }),
      );
    },
    changeCollecedAt(state, action: PayloadAction<string | undefined>) {
      state.journalGroup.collectedAt = action.payload;
    },
    changeMetaField(
      state,
      action: PayloadAction<
        MetaFieldChange<JournalMeta> & { journalId: number }
      >,
    ) {
      const journal = state.journalGroup.journals?.find(
        (j) => j.id === action.payload.journalId,
      );
      if (journal) {
        const meta = journal?.meta ?? {};
        journal.meta = {
          ...meta,
          [action.payload.fieldName]: action.payload.value,
        };
      }
    },
    changeGroupMetaField(
      state,
      action: PayloadAction<MetaFieldChange<JournalGroupMeta>>,
    ) {
      let meta = state.journalGroup.meta ?? {};
      meta = { ...meta, [action.payload.fieldName]: action.payload.value };
      state.journalGroup.meta = meta;
    },
    addJournal(state) {
      if (!state.journalGroup.journals) {
        state.journalGroup.journals = [];
      }
      const sortOrders = state.journalGroup.journals?.map(
        (j) => j.meta?.sortOrder ?? 0,
      );
      const sortOrder =
        Math.max(...(sortOrders?.length ? sortOrders : [0])) + 1;
      const pausedUntil = state.journalGroup.journals?.find(
        (j) => j.pausedUntil,
      )?.pausedUntil;
      state.journalGroup.journals.push(
        createJournal({
          id: id--,
          meta: { sortOrder },
          pausedUntil,
          analysisGroupInstances:
            state.selectedProductAnalyses?.map((ag) =>
              createAnalysisGroupInstance({
                analysisGroupId: ag.id,
                selected: ag.selected,
              }),
            ) ?? [],
        }),
      );
    },
    deleteJournal(state, action: PayloadAction<{ journalId: number }>) {
      state.journalGroup.journals = state.journalGroup.journals?.filter(
        (journal) => journal.id !== action.payload.journalId,
      );
      if (state.journalGroup.journals?.length === 0) {
        state.journalGroup.journals.push(
          createJournal({
            id: id--,
            meta: { sortOrder: 1 },
            analysisGroupInstances:
              state.selectedProductAnalyses?.map((ag) =>
                createAnalysisGroupInstance({
                  analysisGroupId: ag.id,
                  selected: ag.selected,
                }),
              ) ?? [],
          }),
        );
      }
    },
    selectAnalysis(
      state,
      action: PayloadAction<{
        journalId: number;
        analysisId: number;
        value: boolean;
      }>,
    ) {
      const journal = state.journalGroup.journals?.find(
        (j) => j.id === action.payload.journalId,
      );
      if (journal) {
        let analysisGroup = journal.analysisGroupInstances.find(
          (ag) => ag.analysisGroupId === action.payload.analysisId,
        );
        if (!analysisGroup) {
          analysisGroup = createAnalysisGroupInstance({
            analysisGroupId: action.payload.analysisId,
            selected: action.payload.value,
          });
          journal.analysisGroupInstances.push(analysisGroup);
        } else {
          analysisGroup.selected = action.payload.value;
        }
      }
    },
    selectAnalysisAll(
      state,
      action: PayloadAction<{ analysisGroupId: number; value: boolean }>,
    ) {
      const selectedProductAnalyses = state.selectedProductAnalyses;
      if (selectedProductAnalyses) {
        const selectedProductAnalysis = selectedProductAnalyses.find(
          (ag) => ag.id === action.payload.analysisGroupId,
        );
        if (selectedProductAnalysis) {
          selectedProductAnalysis.selected = action.payload.value;
        }
        const journalGroup = state.journalGroup;
        if (journalGroup?.journals) {
          for (const journal of journalGroup.journals) {
            const analysisGroup = journal.analysisGroupInstances.find(
              (ag) => ag.analysisGroupId === action.payload.analysisGroupId,
            );
            if (analysisGroup) {
              analysisGroup.selected = action.payload.value;
            } else {
              journal.analysisGroupInstances.push(
                createAnalysisGroupInstance({
                  analysisGroupId: action.payload.analysisGroupId,
                  selected: action.payload.value,
                }),
              );
            }
          }
        }
      }
    },
    selectProductPackage(
      state,
      action: PayloadAction<{ productPackage: ProductPackage }>,
    ) {
      const selectedProductAnalyses = state.selectedProductAnalyses;
      if (selectedProductAnalyses) {
        const journalGroup = state.journalGroup;
        for (const ag of selectedProductAnalyses) {
          ag.selected = action.payload.productPackage.selectedAnalyses.includes(
            ag.id,
          );
          const invoice =
            (action.payload.productPackage.invoice?.indexOf(ag.id) ?? -1) > -1;
          journalGroup.quickPackageId = action.payload.productPackage.id;
          journalGroup.invoiceGroups = selectInvoiceHelper(
            state,
            ag.id,
            !ag.selected || invoice,
          );

          if (journalGroup?.journals) {
            const packageMeta = action.payload.productPackage.metaFields ?? {};
            for (const journal of journalGroup.journals) {
              journal.meta = {
                ...packageMeta,
              };
              const analysisGroup = journal.analysisGroupInstances.find(
                (group) => group.analysisGroupId === ag.id,
              );
              if (analysisGroup) {
                analysisGroup.selected = ag.selected;
              } else {
                journal.analysisGroupInstances.push(
                  createAnalysisGroupInstance({
                    analysisGroupId: ag.id,
                    selected: ag.selected,
                  }),
                );
              }
            }
          }
        }
      }
    },
    changeAnalysisGroup(
      state,
      action: PayloadAction<{
        journalId: number;
        analysisGroupId: number;
        groupNumber: number | undefined;
      }>,
    ) {
      const journal = state.journalGroup.journals?.find(
        (j) => j.id === action.payload.journalId,
      );
      if (journal) {
        let analysisGroup = journal.analysisGroupInstances.find(
          (ag) => ag.analysisGroupId === action.payload.analysisGroupId,
        );
        if (!analysisGroup) {
          analysisGroup = createAnalysisGroupInstance({
            analysisGroupId: action.payload.analysisGroupId,
            groupnr: action.payload.groupNumber,
          });
          journal.analysisGroupInstances.push(analysisGroup);
        } else {
          analysisGroup.groupnr = action.payload.groupNumber;
        }
      }
    },
    selectAnalysisInvoice(
      state,
      action: PayloadAction<{ analysisGroupId: number; value: boolean }>,
    ) {
      state.journalGroup.invoiceGroups = selectInvoiceHelper(
        state,
        action.payload.analysisGroupId,
        action.payload.value,
      );
    },
    selectStep(state, action: PayloadAction<RegistrationStep>) {
      state.currentStep = action.payload;
    },
    setInternalNote(state, action: PayloadAction<string>) {
      state.journalGroup.note = action.payload;
    },
    setCustomerNote(state, action: PayloadAction<string>) {
      state.journalGroup.customerNote = action.payload;
    },
    addMailJob(state, action: PayloadAction<MailJob>) {
      if (state.journalGroup.mailJobs === undefined) {
        state.journalGroup.mailJobs = [];
      }
      state.journalGroup.mailJobs.push(action.payload);
    },
    selectMailJob(state, action: PayloadAction<MailJob>) {
      if (state.journalGroup.mailJobs) {
        const index = state.journalGroup?.mailJobs?.findIndex(
          (j) => j.email === action.payload.email,
        );
        if (index !== undefined && index >= 0) {
          state.journalGroup.mailJobs[index] = action.payload;
        }
      }
    },
    changeIdentifier(
      state,
      action: PayloadAction<{ identifier: string; journalId: number }>,
    ) {
      const journal = state.journalGroup.journals?.find(
        (j) => j.id === action.payload.journalId,
      );
      if (journal) {
        journal.identifier = action.payload.identifier;
      }
    },
    selectAnimal(
      state,
      action: PayloadAction<{ animal: Animal | undefined; journalId: number }>,
    ) {
      const journal = state.journalGroup.journals?.find(
        (j) => j.id === action.payload.journalId,
      );
      if (journal) {
        journal.identifier = action.payload.animal?.name;
        journal.animal = action.payload.animal;
        journal.animalId = action.payload.animal?.id;
        if (action.payload.animal?.meta) {
          journal.meta = {
            ...(journal?.meta ?? {}),
            ...action.payload.animal.meta,
          };
        }
      }
    },
    selectVeterinarian(
      state,
      action: PayloadAction<{
        veterinarian: Veterinarian | undefined;
      }>,
    ) {
      state.journalGroup.veterinarianId = action.payload.veterinarian?.id;
    },
    setMailJobs(state, action: PayloadAction<MailJob[] | undefined>) {
      state.journalGroup.mailJobs = action.payload;
    },
    setRegistrationId(state, action: PayloadAction<number | undefined>) {
      state.registrationId = action.payload;
    },
    setFollowup(state, action: PayloadAction<boolean>) {
      state.journalGroup.followup = action.payload;
    },
    setFollowupJournal(state, action: PayloadAction<string>) {
      state.journalGroup.followupJournal = action.payload;
    },
    addExtra(state, action: PayloadAction<number>) {
      if (!state.selectedProductAnalyses) {
        return;
      }
      const index = state.selectedProductAnalyses.findIndex(
        (analysisGroup) => analysisGroup.id === action.payload,
      );
      if (index >= 0) {
        const analysisGroup = {
          ...state.selectedProductAnalyses[index],
          extra: false,
        };
        state.selectedProductAnalyses[index] = analysisGroup;
      }
    },
    markNoteForDeletion(state, action: PayloadAction<number>) {
      const note =
        state.journalGroup.notes?.find((n) => n.id === action.payload) ??
        state.journalGroup.customerNotes?.find((n) => n.id === action.payload);
      if (note) {
        note.delete = true;
      }
    },
    setCustomer(state, action: PayloadAction<{ customerId: number }>) {
      state.journalGroup.customerId = action.payload.customerId;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchAutofill.fulfilled, (state, action) => {
        state.autofills[action.payload.key] = action.payload.content;
      })
      .addCase(applyPreregistration.fulfilled, (state, action) => {
        state.journalGroup = action.payload;
        state.selectedProductFamilyId = action.payload.productFamilyId;
      })
      .addCase(clearRegistration.fulfilled, (state, action) => {
        state.journalGroup = {
          productFamilyId: state.selectedProductFamilyId,
          productId: state.selectedProductId,
        };
        if (action.payload.step) {
          state.currentStep = action.payload.step;
        }
        const product = action.payload.product;
        if (product && action.payload.productAnalyses) {
          state.journalGroup.invoiceGroups = createInvoiceGroups(
            action.payload.productAnalyses,
            product,
          );
          state.journalGroup.journals = [
            createJournal({
              id: id--,
              analysisGroupInstances: action.payload.productAnalyses.map((ag) =>
                createAnalysisGroupInstance({
                  analysisGroupId: ag.id,
                  selected: selected(ag, product),
                  invoice: invoice(ag, product),
                }),
              ),
            }),
          ];
          state.selectedProductId = product.id;
          state.selectedProductAnalyses = action.payload.productAnalyses.map(
            (ag) => ({ ...ag, selected: selected(ag, product) }),
          );
        }
        state.registrationId = undefined;
      })
      .addCase(doSaveJournal.pending, (state) => {
        state.saving = true;
      })
      .addCase(doSaveJournal.rejected, (state) => {
        state.saving = false;
      })
      .addCase(doSaveJournal.fulfilled, (state, action) => {
        if (!action.payload.clear) {
          state.journalGroup = action.payload.journalGroup;
        }
        if (state.journalGroup.journals) {
          state.journalGroup.journals.sort(journalSort);
          state.saving = false;
        }
      })
      .addCase(loadJournal.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadJournal.rejected, (state) => {
        state.loading = false;
      })
      .addCase(loadJournal.fulfilled, (state, action) => {
        state.journalGroup = { ...action.payload };
        if (state.journalGroup.journals) {
          state.journalGroup.journals.sort(journalSort);
          state.selectedProductAnalyses = state.selectedProductAnalyses?.map(
            (ag) => {
              const isSelected =
                ag.selected ||
                !!state.journalGroup.journals?.find(
                  (j) =>
                    j.analysisGroupInstances.find(
                      (agi) => agi.analysisGroupId === ag.id,
                    )?.selected,
                );
              return {
                ...ag,
                selected: isSelected,
                extra: ag.extra && !isSelected,
              };
            },
          );
        }
        state.loading = false;
      })
      .addCase(pausAnalysis.fulfilled, (state, action) => {
        const journalGroup = state.journalGroup;
        if (journalGroup?.journals) {
          for (const journal of journalGroup.journals) {
            if (!journal.pausedUntil) {
              journal.pausedUntil = action.payload.pausedUntil;
            } else {
              journal.pausedUntil = undefined;
            }
          }
        }
      })
      .addCase(changeLabnumber.fulfilled, (state, action) => {
        const journal = state.journalGroup.journals?.find(
          (j) => j.id === action.payload.journalId,
        );
        if (journal) {
          journal.labnumber = action.payload.labnumber;
        }
      })
      .addCase(validateLabnumber.fulfilled, (state, action) => {
        const journal = state.journalGroup.journals?.find(
          (j) => j.id === action.payload.journalId,
        );
        if (journal) {
          if (action.payload.labnumber === journal.labnumber) {
            journal.validLabnumber = action.payload.valid.free;
            state.registrationId = action.payload.valid.registration;
          }
        }
      }),
});

export const {
  selectProductFamily,
  selectProduct,
  changeCollecedAt,
  changeMetaField,
  changeGroupMetaField,
  changeIdentifier,
  selectAnalysis,
  selectAnalysisAll,
  changeAnalysisGroup,
  selectAnalysisInvoice,
  selectStep,
  setInternalNote,
  setCustomerNote,
  selectMailJob,
  addMailJob,
  deleteJournal,
  selectAnimal,
  selectVeterinarian,
  setMailJobs,
  setFollowup,
  setFollowupJournal,
  addExtra,
  addJournal,
  markNoteForDeletion,
  selectProductPackage,
  setRegistrationId,
  setCustomer,
} = registrationSlice.actions;

export default registrationSlice.reducer;

function createInvoiceGroups(
  productAnalyses: Readonly<IAnalysisGroup>[],
  product: IProduct,
): { id: number; code: string }[] {
  return productAnalyses
    .filter((pa) => invoice(pa, product))
    .map((pa) => {
      return { id: pa.id, code: pa.code };
    });
}
